import React, { useState, useMemo } from 'react';
import { Table, Tag, Avatar, Spin, Button, Dropdown, message, Modal } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useQueryClient } from '@tanstack/react-query';
import { useFirebaseUsers, useMusicians, useBands } from '../../api/queries';
import { send_password_reset, disable_user, enable_user, unlink_musician, unlink_band, sync_contributors } from '../../api';
import type { FirebaseUser, Musician, Band } from '../../api';
import { DefaultAvatar } from '../../components/icons';
import './UserManagement.css';

interface UserWithLinks extends FirebaseUser {
  linkedMusician?: Musician | null;
  linkedBands?: Band[];
}

const UserManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: users, isLoading: usersLoading, error: usersError } = useFirebaseUsers();
  const { data: musicians, isLoading: musiciansLoading } = useMusicians();
  const { data: bands, isLoading: bandsLoading } = useBands();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [syncLoading, setSyncLoading] = useState(false);

  // Create a map of user_id -> musician for quick lookup
  const musiciansByUserId = useMemo(() => {
    const map = new Map<string, Musician>();
    if (musicians) {
      for (const musician of musicians) {
        if (musician.user_id) {
          map.set(musician.user_id, musician);
        }
      }
    }
    return map;
  }, [musicians]);

  // Create a map of claimed_by_user_id -> bands for quick lookup
  const bandsByClaimedUserId = useMemo(() => {
    const map = new Map<string, Band[]>();
    if (bands) {
      for (const band of bands) {
        if (band.claimed_by_user_id) {
          const existing = map.get(band.claimed_by_user_id) || [];
          existing.push(band);
          map.set(band.claimed_by_user_id, existing);
        }
      }
    }
    return map;
  }, [bands]);

  // Merge users with their linked musicians and bands
  const usersWithLinks: UserWithLinks[] = useMemo(() => {
    if (!users) return [];
    return users.map(user => ({
      ...user,
      linkedMusician: musiciansByUserId.get(user.uid) || null,
      linkedBands: bandsByClaimedUserId.get(user.uid) || []
    }));
  }, [users, musiciansByUserId, bandsByClaimedUserId]);

  const handleResetPassword = async (user: FirebaseUser) => {
    setActionLoading(user.uid);
    try {
      const result = await send_password_reset(user.uid);
      message.success(`Password reset email sent to ${result.email}`);
    } catch (err: any) {
      message.error(err.message || 'Failed to send password reset email');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleDisabled = async (user: FirebaseUser) => {
    const action = user.disabled ? 'enable' : 'disable';

    Modal.confirm({
      title: `${user.disabled ? 'Enable' : 'Disable'} User`,
      content: user.disabled
        ? `Are you sure you want to enable ${user.email}? They will be able to sign in again.`
        : `Are you sure you want to disable ${user.email}? They will not be able to sign in.`,
      okText: user.disabled ? 'Enable' : 'Disable',
      okButtonProps: { danger: !user.disabled },
      onOk: async () => {
        setActionLoading(user.uid);
        try {
          if (user.disabled) {
            await enable_user(user.uid);
            message.success(`User ${user.email} has been enabled`);
          } else {
            await disable_user(user.uid);
            message.success(`User ${user.email} has been disabled`);
          }
          queryClient.invalidateQueries({ queryKey: ['firebase-users'] });
        } catch (err: any) {
          message.error(err.message || `Failed to ${action} user`);
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  const handleUnlinkMusician = async (user: UserWithLinks) => {
    if (!user.linkedMusician) return;

    const musicianName = `${user.linkedMusician.first_name} ${user.linkedMusician.last_name}`;

    Modal.confirm({
      title: 'Unlink Musician Profile',
      content: (
        <div>
          <p>Are you sure you want to unlink the musician profile <strong>{musicianName}</strong> from {user.email}?</p>
          <p style={{ color: '#faad14', marginTop: 8 }}>This will also remove any band roles the user has from this musician's band memberships.</p>
        </div>
      ),
      okText: 'Unlink',
      okButtonProps: { danger: true },
      onOk: async () => {
        setActionLoading(user.uid);
        try {
          await unlink_musician(user.uid);
          message.success(`Musician profile "${musicianName}" has been unlinked from ${user.email}`);
          queryClient.invalidateQueries({ queryKey: ['firebase-users'] });
          queryClient.invalidateQueries({ queryKey: ['musicians'] });
        } catch (err: any) {
          message.error(err.message || 'Failed to unlink musician');
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  const handleUnlinkBand = async (user: UserWithLinks, band: Band) => {
    Modal.confirm({
      title: 'Unlink Band',
      content: (
        <div>
          <p>Are you sure you want to unlink the band <strong>{band.name}</strong> from {user.email}?</p>
          <p style={{ color: '#faad14', marginTop: 8 }}>This will remove the band-admin role for this band from the user.</p>
        </div>
      ),
      okText: 'Unlink',
      okButtonProps: { danger: true },
      onOk: async () => {
        setActionLoading(user.uid);
        try {
          await unlink_band(user.uid, band.id);
          message.success(`Band "${band.name}" has been unlinked from ${user.email}`);
          queryClient.invalidateQueries({ queryKey: ['firebase-users'] });
          queryClient.invalidateQueries({ queryKey: ['bands'] });
        } catch (err: any) {
          message.error(err.message || 'Failed to unlink band');
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  const handleSyncContributors = async () => {
    setSyncLoading(true);
    try {
      const result = await sync_contributors();
      message.success(
        `Sync complete: ${result.contributorsCreated} created, ${result.contributorsSkipped} already existed`
      );
      if (result.errors.length > 0) {
        console.error('Sync errors:', result.errors);
        message.warning(`${result.errors.length} errors occurred during sync. Check console for details.`);
      }
    } catch (err: any) {
      message.error(err.message || 'Failed to sync contributors');
    } finally {
      setSyncLoading(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return '#ff4d4f';
      case 'musician': return '#1890ff';
      case 'fan': return '#52c41a';
      default: return '#8c8c8c';
    }
  };

  const columns: ColumnsType<UserWithLinks> = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <div className="user-table__user-cell">
          {record.photoURL ? (
            <Avatar src={record.photoURL} size={36} />
          ) : (
            <div className="user-table__avatar-placeholder">
              <DefaultAvatar size={20} />
            </div>
          )}
          <div className="user-table__user-info">
            <div className="user-table__name">{record.displayName || 'No name'}</div>
            <div className="user-table__email">{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Linked Musician',
      key: 'musician',
      width: 150,
      render: (_, record) => (
        record.linkedMusician ? (
          <span className="user-table__link-name">
            {record.linkedMusician.first_name} {record.linkedMusician.last_name}
          </span>
        ) : (
          <span className="user-table__no-link">-</span>
        )
      ),
    },
    {
      title: 'Claimed Bands',
      key: 'bands',
      width: 150,
      render: (_, record) => (
        record.linkedBands && record.linkedBands.length > 0 ? (
          <div className="user-table__bands">
            {record.linkedBands.map((band, idx) => (
              <span key={band.id} className="user-table__link-name">
                {band.name}{idx < record.linkedBands!.length - 1 ? ', ' : ''}
              </span>
            ))}
          </div>
        ) : (
          <span className="user-table__no-link">-</span>
        )
      ),
    },
    {
      title: 'Roles',
      key: 'roles',
      width: 100,
      render: (_, record) => (
        <div className="user-table__roles">
          {record.claims?.roles?.length ? (
            record.claims.roles.map(role => (
              <Tag key={role} color={getRoleColor(role)}>{role}</Tag>
            ))
          ) : (
            <Tag color={getRoleColor('fan')}>fan</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 80,
      render: (_, record) => (
        <Tag color={record.disabled ? 'error' : 'success'}>
          {record.disabled ? 'Disabled' : 'Active'}
        </Tag>
      ),
    },
    {
      title: 'Joined',
      key: 'joined',
      width: 95,
      render: (_, record) => (
        <span className="user-table__date">
          {record.creationTime ? new Date(record.creationTime).toLocaleDateString() : '-'}
        </span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_, record) => {
        const menuItems: any[] = [
          {
            key: 'reset',
            label: 'Send Password Reset',
            onClick: () => handleResetPassword(record),
          },
          {
            type: 'divider',
          },
          {
            key: 'toggle',
            label: record.disabled ? 'Enable User' : 'Disable User',
            danger: !record.disabled,
            onClick: () => handleToggleDisabled(record),
          },
        ];

        // Add unlink musician option if user has a linked musician
        if (record.linkedMusician) {
          menuItems.push(
            { type: 'divider' },
            {
              key: 'unlink-musician',
              label: 'Unlink Musician Profile',
              danger: true,
              onClick: () => handleUnlinkMusician(record),
            }
          );
        }

        // Add unlink band options if user has claimed bands
        if (record.linkedBands && record.linkedBands.length > 0) {
          menuItems.push({ type: 'divider' });

          if (record.linkedBands.length === 1) {
            menuItems.push({
              key: `unlink-band-${record.linkedBands[0].id}`,
              label: `Unlink Band: ${record.linkedBands[0].name}`,
              danger: true,
              onClick: () => handleUnlinkBand(record, record.linkedBands![0]),
            });
          } else {
            // Multiple bands - create a submenu
            menuItems.push({
              key: 'unlink-bands',
              label: 'Unlink Band',
              danger: true,
              children: record.linkedBands.map(band => ({
                key: `unlink-band-${band.id}`,
                label: band.name,
                onClick: () => handleUnlinkBand(record, band),
              })),
            });
          }
        }

        return (
          <Dropdown
            trigger={['click']}
            menu={{ items: menuItems }}
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              loading={actionLoading === record.uid}
            />
          </Dropdown>
        );
      },
    },
  ];

  const isLoading = usersLoading || musiciansLoading || bandsLoading;

  if (isLoading) {
    return (
      <div className="user-management">
        <div className="user-management__loading">
          <Spin size="large" />
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  if (usersError) {
    return (
      <div className="user-management">
        <h1 className="user-management__title">User Management</h1>
        <p className="user-management__error">Error loading users: {usersError.message}</p>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management__header">
        <div>
          <h1 className="user-management__title">User Management</h1>
          <p className="user-management__count">{users?.length || 0} users</p>
        </div>
        <Button
          onClick={handleSyncContributors}
          loading={syncLoading}
        >
          Sync Contributors
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={usersWithLinks}
        rowKey="uid"
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: 950 }}
        className="user-management__table"
      />
    </div>
  );
};

export default UserManagement;
